import { Know } from 'interfaces/Know'
import CloseIcon from 'assets/icons/close.svg'
import { Body1, Body2, Caption } from 'stories/elements/Typography/Text'
import Stack from 'components/elements/Stack'
import ProfilePicture from 'components/elements/ProfilePicture'
import Row from 'components/elements/Row'
import { returnKnowerFollowerLabel } from 'lib/formatting/knows'
import LikeIcon from 'assets/icons/webapp_like.svg'
import ViewsIcon from 'assets/icons/webapp_views.svg'
import CommentIcon from 'assets/icons/webapp_comment.svg'
import router from 'next/router'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import { GradeBadge, KnowTypeBadge, SubjectBadge } from 'components/elements/Badge'
import _useTranslation from 'lib/hooks/_useTranslation'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import KnowDisplayCard from 'components/modules/Knows/KnowDisplayCard'
import { AnalyticsEventSimilarKnowsButtonClick } from 'lib/constants/AnalyticsEventType'
import { trackEvent } from 'lib/tracking/analytics-service'
import { KnowerLink } from 'lib/features/Know/knower'

const SidebarClose = styled(CloseIcon)`
    position: absolute;
    right: 0px;
    top: 15px;
    z-index: ${theme.zIndex.FAB};
    cursor: pointer;
`

const Container = styled.div`
    .sidebar-cover {
        position: relative;
        width: 250px;
        top: 0px;
        height: 100%;
        background-color: ${theme.colors.white};
        z-index: 2;
        border-left: 1px solid ${theme.colors.violet};
    }
    .sidebar-wrapper {
        overflow: hidden;
        z-index: 2;
        width: 245px;

        height: auto;
        position: absolute;
        top: 0px;
    }
    .sidebar {
        border-left: 1px solid ${theme.colors.violet};
        z-index: 2;
        width: 250px;
        max-height: auto;
        background-color: ${theme.colors.white};
        display: flex;
        flex-direction: column;

        justify-content: start;
        gap: 0px;

        .actions {
            position: relative;
            padding: 45px 15px;
            background-color: ${theme.colors.white};
            display: grid;
            gap: 20px;

            .profile {
                width: fit-content;
                display: grid;
                grid-template-columns: auto 1fr;
                gap: 20px;
                padding: 5px;
            }
            .badges {
                display: flex;
                flex-wrap: wrap;
                gap: 5px;
            }
            .stats {
                display: flex;
                padding: 0 10px;
                gap: 10px;
                flex-wrap: wrap;
            }
            .separator {
                height: 1px;
                width: 100%;
                border-top: 1px solid ${theme.colors.violetDarkerBackground};
            }
        }
    }
`

const SimilarKnowsGrid = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    p {
        width: 100%;
    }
`

interface Props {
    know: Know
    similarKnows: Know[] | null
    closePreview: () => void
}

const KnowContentPreviewSidebar = (props: Props) => {
    const { t } = _useTranslation('knows')
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const referrerScreen = 'knowPreviewSidebar'

    const showSidebarConversionElement = props.similarKnows?.length

    const Badges = () => (
        <div className="badges">
            {props.know?.subjects?.length ? <SubjectBadge subject={props.know.subjects[0]} isSmall /> : null}
            <GradeBadge grades={props.know.grades} isSmall />
            {props.know.knowType ? <KnowTypeBadge knowType={props.know.knowType} isSmall /> : null}
        </div>
    )

    const onSimilarKnowsClicked = (know: Know) => {
        trackEvent(AnalyticsEventSimilarKnowsButtonClick, { know, referrerScreen })
    }

    return (
        <Container>
            <div className="sidebar-cover" />
            <div className="sidebar-wrapper">
                <div className="sidebar">
                    <div className="actions">
                        <SidebarClose onClick={props.closePreview} />
                        <Stack gutter={10}>
                            <Caption fontFamily="var(--font-inter)" fontWeight={400}>
                                {t('knows/contentPreviewSidebarUploadedByLabel')}
                            </Caption>
                            <KnowerLink knower={props.know.knower}>
                                <div className="profile">
                                    <ProfilePicture
                                        user={props.know.knower.user}
                                        size={40}
                                        borderColor={theme.colors.outlineGray}
                                    />
                                    <Stack gutter={2}>
                                        <Body2
                                            color={theme.colors.headlineDark}
                                            fontFamily="var(--font-inter)"
                                            fontWeight={600}
                                        >
                                            {props.know.knower.user.name ?? props.know.knower.user.username}
                                        </Body2>
                                        <Caption
                                            color={theme.colors.body1Black}
                                            fontWeight={400}
                                            fontFamily="var(--font-inter)"
                                        >
                                            {returnKnowerFollowerLabel(props.know, t, router.locale)}
                                        </Caption>
                                    </Stack>
                                </div>
                            </KnowerLink>
                            <div className="stats">
                                <Row gap={5}>
                                    <LikeIcon />
                                    <Body2 color={theme.colors.headlineDark} fontFamily="var(--font-inter)">
                                        {props.know.likes}
                                    </Body2>
                                </Row>
                                <Row gap={5}>
                                    <ViewsIcon />
                                    <Body2 color={theme.colors.headlineDark} fontFamily="var(--font-inter)">
                                        {props.know.views}
                                    </Body2>
                                </Row>
                                <Row gap={5}>
                                    <CommentIcon />
                                    <Body2 color={theme.colors.headlineDark} fontFamily="var(--font-inter)">
                                        {props.know.commentsCount}
                                    </Body2>
                                </Row>
                            </div>
                        </Stack>
                        <div className="separator" />
                        <Stack gutter={10}>
                            <Caption fontFamily="var(--font-inter)" fontWeight={400}>
                                {t('knows/createDetailsPageKnowTitleLabel')}
                            </Caption>
                            <Body1 color={theme.colors.headlineDark} fontFamily="var(--font-inter)">
                                {props.know.seoTitle ?? props.know.title}
                            </Body1>
                        </Stack>
                        <div className="separator" />
                        <Stack gutter={10}>
                            <Caption fontFamily="var(--font-inter)" fontWeight={400}>
                                {t('knows/contentPreviewSidebarTagsLabel')}
                            </Caption>
                            <Badges />
                        </Stack>
                        {!authenticatedUser ? (
                            <>
                                <div className="separator" />
                                <Stack gutter={10}>
                                    <Caption fontFamily="var(--font-inter)" fontWeight={400}>
                                        {t('knows/knowDetailsComments')}
                                    </Caption>
                                    <Body2 color={theme.colors.subtitleBlack} fontFamily="var(--font-inter)">
                                        {t('knows/contentPreviewSidebarLoginToSeeCommentsLabel')}
                                    </Body2>
                                </Stack>
                            </>
                        ) : null}
                        {showSidebarConversionElement ? (
                            <>
                                <div className="separator" />
                                <SimilarKnowsGrid>
                                    <Caption fontFamily="var(--font-inter)" fontWeight={400} textAlign="left">
                                        {t('knows/similarContentHeading')}
                                    </Caption>
                                    {props.similarKnows?.map((know) => (
                                        <span key={know.uuid} onClick={() => onSimilarKnowsClicked(know)}>
                                            <KnowDisplayCard key={know.uuid} know={know} referrerScreen={referrerScreen} />
                                        </span>
                                    ))}
                                </SimilarKnowsGrid>
                            </>
                        ) : null}
                    </div>
                </div>
            </div>
        </Container>
    )
}

export default KnowContentPreviewSidebar
